import type { InternalRefetchQueriesInclude } from '@apollo/client';
import { gql } from '@apollo/client';
import { Alert, Box, Checkbox, FormField, Paragraph, Textarea, TextButton } from '@zillow/constellation-10';
import {
  AdornedInput,
  Adornment,
  DatePickerInput,
  IconCalendarOutline,
  IconCheckmarkCircle,
  IconLock,
  Label,
} from '@zillow/constellation';
import { format } from 'date-fns';
import type React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import type { ConversationList_GetConversationsQuery } from '../../types/GraphqlTypes';
import {
  ConversationList_GetConversationsDocument,
  NotesModal_GetNewStatusLabelsDocument,
  RenterProgressStatus,
  useNotesModal_DeleteNoteMutation,
  useNotesModal_GetNotesDataQuery,
  useNotesModal_UpdateConversationAttributesMutation,
} from '../../types/GraphqlTypes';
import { EVENT_TRACKING_CATEGORIES } from '../../constants/AnalyticsConstants';
import {
  emitApplicationSentCheckboxClickEvent,
  emitNoteSavedEvent,
  emitScheduleTourEvent,
} from '../../constants/clickstreamEvents/notesModal';
import StringConstants from '../../constants/StringConstants';
import { MAX_OLD_CONVERSATION_DAYS } from '../../constants/ConfigConstants';
import { AppContext } from '../../contexts/AppContextProvider';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import dateTimeUtils from '../../utils/dateTime/dateTimeUtils';
import TimePicker from '../TimePicker';
import { TooltipWrapper } from '@zg-rentals/satellite-components';
import { StyledLabeledControl } from './StyledComponents';
import {
  emitEditTourDateEvent,
  emitEditTourTimeEvent,
  emitMakeANoteEvent,
} from '../../constants/clickstreamEvents/renterDetailsModal';
import { logApolloError } from '../../utils/errorUtils';
import { logError } from '@zg-rentals/log-error';
import { useAuth } from '@zg-rentals/auth-comet';
import useEventTrackingCategory from '../../hooks/useEventTrackingCategory';

const PRIVACY_TEXT = 'Any information you add here is only visible to you.';
const ERROR_MESSAGE = 'Sorry, there was an error. Try refreshing the page.';
const TEXT_AREA_PLACEHOLDER =
  'e.g. Rachel and I spoke on the phone 8/8. She has a roommate and a small, hypoallergenic dog.';
const DATE_PICKER_FORMAT = 'MM/dd/yyyy';
const INVALID_TOUR_MESSAGE = `Invalid "Tour Scheduled" date. Please fix and try again.`;

type Props = {
  clientTimeZone: string;
  conversationId: string;
  listingAlias: string;
  shouldRefetchStatusLabels: boolean;
  resetAllInlineFeedbackChildState?: boolean;
  onResetAllInlineFeedbackChildState?: () => void;
  shouldSaveNoteText?: boolean;
  onHandleNoteSavingComplete?: () => void;
};

const Notepad = ({
  clientTimeZone,
  conversationId,
  listingAlias,
  shouldRefetchStatusLabels,
  resetAllInlineFeedbackChildState = false,
  onResetAllInlineFeedbackChildState = () => {},
  shouldSaveNoteText = false,
  onHandleNoteSavingComplete = () => {},
}: Props): JSX.Element | null => {
  const { isImpersonator } = useContext(AppContext);
  const { daysBack, folder, isByProperty, readRepliedStatus } = useContext(InboxPageContext);
  const { data: notepadData } = useNotesModal_GetNotesDataQuery({
    variables: {
      conversationId,
      listingAlias,
    },
    onError: (error) => {
      logApolloError(error, {
        details: 'NotesModal - Error fetching data for the notepad',
        conversationId,
        listingAlias,
      });
    },
    fetchPolicy: 'cache-first',
  });

  const { user } = useAuth();
  const eventTrackingCategory = useEventTrackingCategory();

  const notesData = notepadData?.ZRM_getConversationById;
  const hasZrmToursEnabled = notesData?.touring.hasZrmToursEnabled ?? false;
  const applicationAlreadySent = notesData?.applicationAlreadySent || false;
  const applicationMarkedSent = notesData?.attributes?.applicationMarkedSent || false;
  const tourScheduledTimestampMs = notesData?.attributes?.tourScheduledTimestampMs || '';
  const formattedTourScheduledDate = tourScheduledTimestampMs
    ? format(Number(tourScheduledTimestampMs), DATE_PICKER_FORMAT)
    : '';
  const noteDetails = notesData?.note;

  const [inlineFeedbackForApplicationSent, setInlineFeedbackForApplicationSent] = useState(false);
  const [inlineFeedbackForTourScheduled, setInlineFeedbackForTourScheduled] = useState(false);
  const [inlineFeedbackForNoteUpdated, setInlineFeedbackForNoteUpdated] = useState(false);
  const [showAlertMessage, setShowAlertMessage] = useState(false);
  const [customizedAlertMessage, setCustomizedAlertMessage] = useState('');

  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [noteText, setNoteText] = useState<string | null | undefined>('');
  const [noteNeedsSaving, setNoteNeedsSaving] = useState(false);

  const resetAllInlineFeedbackState = useCallback(() => {
    setInlineFeedbackForNoteUpdated(false);
    setInlineFeedbackForTourScheduled(false);
    setInlineFeedbackForApplicationSent(false);
    setShowAlertMessage(false);
    setCustomizedAlertMessage('');
  }, []);

  useEffect(() => {
    if (notepadData) {
      setNoteText(noteDetails?.note);
      setIsCheckboxChecked(applicationAlreadySent || applicationMarkedSent || false);
    }
  }, [notepadData, listingAlias]);

  useEffect(() => {
    if (resetAllInlineFeedbackChildState) {
      resetAllInlineFeedbackState();
      onResetAllInlineFeedbackChildState();
    }
  }, [resetAllInlineFeedbackChildState, onResetAllInlineFeedbackChildState]);

  useEffect(() => {
    if (shouldSaveNoteText) {
      saveNoteText();
      onHandleNoteSavingComplete();
    }
  }, [shouldSaveNoteText, onHandleNoteSavingComplete]);

  const [updateConversationAttributes] = useNotesModal_UpdateConversationAttributesMutation({
    ...((shouldRefetchStatusLabels && {
      refetchQueries: [
        {
          query: NotesModal_GetNewStatusLabelsDocument,
          variables: {
            conversationId,
            listingAlias,
            clientTimeZone,
          },
        },
      ],
    }) as { refetchQueries: InternalRefetchQueriesInclude }),
  });

  const [deleteNote] = useNotesModal_DeleteNoteMutation({
    onError() {
      setShowAlertMessage(true);
    },
    onCompleted() {
      setShowAlertMessage(false);
      setInlineFeedbackForNoteUpdated(true);
    },
  });

  const saveNoteText = async (): Promise<void> => {
    setNoteNeedsSaving(false);

    if (noteNeedsSaving) {
      if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.LEAD_MANAGEMENT) {
        emitNoteSavedEvent(user, { listingAlias });
      }

      if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.LEAD_MANAGEMENT_PAGE) {
        emitMakeANoteEvent(user);
      }

      if (!noteText?.trim()) {
        deleteNote({
          variables: {
            conversationId,
            note: '',
          },
          update(cache) {
            cache.modify({
              id: cache.identify({
                __typename: 'ZRM_Note',
                conversationId,
              }),
              fields: {
                note() {
                  return '';
                },
                lastUpdatedMs() {
                  return new Date().getTime();
                },
              },
            });
          },
        });
      } else {
        updateConversationAttributes({
          variables: {
            note: noteText?.trim(),
            conversationId,
            listingAlias,
          },
          update(cache) {
            cache.modify({
              id: cache.identify({
                __typename: 'ZRM_Note',
                conversationId,
              }),
              fields: {
                note() {
                  return noteText?.trim();
                },
                lastUpdatedMs() {
                  return new Date().getTime();
                },
              },
            });
          },
          onError() {
            setShowAlertMessage(true);
          },
          onCompleted() {
            setShowAlertMessage(false);
            setInlineFeedbackForNoteUpdated(true);
          },
        });
      }
    }
  };

  const handleNoteTextChange = (e: React.FormEvent<HTMLTextAreaElement>): void => {
    const { value } = e.currentTarget;
    setNoteText(value);

    if (!noteNeedsSaving) {
      setNoteNeedsSaving(true);
    }

    if (inlineFeedbackForNoteUpdated) {
      setInlineFeedbackForNoteUpdated(false);
    }
  };

  const handleApplicationSentChange = async (): Promise<void> => {
    const newApplicationMarkedSent = !isCheckboxChecked;
    setIsCheckboxChecked(newApplicationMarkedSent);

    if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.LEAD_MANAGEMENT) {
      emitApplicationSentCheckboxClickEvent(user, { listingAlias });
    }

    updateConversationAttributes({
      variables: {
        applicationMarkedSent: newApplicationMarkedSent,
        conversationId,
        listingAlias,
      },
      update(cache) {
        cache.writeFragment({
          id: cache.identify({ conversationId, __typename: 'ZRM_Conversation' }),
          fragment: gql`
            fragment NoteData on ZRM_Conversation {
              attributes {
                tourScheduledTimestampMs
                __typename
                applicationMarkedSent
              }
            }
          `,
          data: {
            attributes: {
              tourScheduledTimestampMs,
              __typename: 'ZRM_ConversationAttributes',
              applicationMarkedSent: newApplicationMarkedSent,
            },
          },
        });

        cache.modify({
          id: cache.identify({
            __typename: 'ZRM_Note',
            conversationId,
          }),
          fields: {
            lastUpdatedMs() {
              return new Date().getTime();
            },
          },
        });
      },
      onError() {
        setShowAlertMessage(true);
      },
      onCompleted() {
        setShowAlertMessage(false);
        setInlineFeedbackForApplicationSent(newApplicationMarkedSent);
      },
    });
  };

  const handleDatePickerInputChange = async (value: string): Promise<void> => {
    const newTimestampMs = dateTimeUtils.updateTimestampMsWithNewDate({
      newDateString: value,
      timestampMsToUpdate: tourScheduledTimestampMs,
    });

    if (newTimestampMs === tourScheduledTimestampMs) {
      return;
    }

    if (newTimestampMs === 'NaN') {
      logError({
        error: new Error('Notepad handleDatePickerInputChange error'),
        context: 'NotesModal - User attempted to updateNote() with tourScheduledTimestampMs of "NaN"',
      });
      setShowAlertMessage(true);
      setCustomizedAlertMessage(INVALID_TOUR_MESSAGE);
    }

    if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.LEAD_MANAGEMENT) {
      emitScheduleTourEvent(user, { listingAlias });
    }

    if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.LEAD_MANAGEMENT_PAGE) {
      emitEditTourDateEvent(user);
    }

    updateConversationAttributes({
      variables: {
        conversationId,
        listingAlias,
        tourScheduledTimestampMs: newTimestampMs,
      },
      update(cache) {
        if (!tourScheduledTimestampMs) {
          // When a tour is scheduled for the first time, clear the cached results
          // from the ConversationList_GetConversationsQuery where renterProgressStatus='Tours'
          cache.evict({
            fieldName: 'ZRM_getLatestConversationsV2',
            args: {
              clientTimeZone,
              folder,
              listingAlias: isByProperty ? listingAlias : null,
              readRepliedStatus,
              renterProgressStatus: RenterProgressStatus.Tours,
            },
          });
          cache.gc();
        }

        cache.writeFragment({
          id: cache.identify({ conversationId, __typename: 'ZRM_Conversation' }),
          fragment: gql`
            fragment NoteData on ZRM_Conversation {
              attributes {
                tourScheduledTimestampMs
                __typename
                applicationMarkedSent
              }
            }
          `,
          data: {
            attributes: {
              tourScheduledTimestampMs: newTimestampMs,
              __typename: 'ZRM_ConversationAttributes',
              applicationMarkedSent,
            },
          },
        });

        cache.modify({
          id: cache.identify({
            __typename: 'ZRM_Note',
            conversationId,
          }),
          fields: {
            lastUpdatedMs() {
              return new Date().getTime();
            },
          },
        });
      },
      onError() {
        setShowAlertMessage(true);
      },
      onCompleted() {
        setShowAlertMessage(false);
        setCustomizedAlertMessage('');
        setInlineFeedbackForTourScheduled(true);
      },
    });
  };

  const handleClearTourScheduledTimestampMs = (): void => {
    // Reset inline feedback
    setInlineFeedbackForTourScheduled(false);
    setShowAlertMessage(false);

    updateConversationAttributes({
      variables: {
        conversationId,
        listingAlias,
        tourScheduledTimestampMs: '',
      },

      update(cache) {
        cache.writeFragment({
          id: cache.identify({ conversationId, __typename: 'ZRM_Conversation' }),
          fragment: gql`
            fragment TourScheduledUpdate on ZRM_Conversation {
              attributes {
                tourScheduledTimestampMs
                __typename
                applicationMarkedSent
              }
            }
          `,
          data: {
            attributes: {
              tourScheduledTimestampMs: '',
              __typename: 'ZRM_ConversationAttributes',
              applicationMarkedSent,
            },
          },
        });

        // Remove the conversation from the cached list of conversations marked as toured
        cache.updateQuery<ConversationList_GetConversationsQuery>(
          {
            query: ConversationList_GetConversationsDocument,
            variables: {
              clientTimeZone,
              daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
              folder,
              listingAlias: isByProperty ? listingAlias : null,
              readRepliedStatus,
              renterProgressStatus: RenterProgressStatus.Tours,
              skipMerge: true,
            },
          },
          (data) => {
            if (!data) return;

            const updatedCachedConversationsMarkedAsToured = data.ZRM_getLatestConversationsV2.conversations.filter(
              (conversation) => conversation.conversationId !== conversationId,
            );
            return {
              ZRM_getLatestConversationsV2: {
                ...data.ZRM_getLatestConversationsV2,
                conversations: updatedCachedConversationsMarkedAsToured || [],
              },
            };
          },
        );
      },
      onError() {
        setShowAlertMessage(true);
      },
    });
  };

  const handleTimePickerChange = (tourScheduledTimestampMs: string): void => {
    if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.LEAD_MANAGEMENT_PAGE) {
      emitEditTourTimeEvent(user);
    }

    updateConversationAttributes({
      variables: {
        conversationId,
        listingAlias,
        tourScheduledTimestampMs,
      },
      update(cache) {
        cache.writeFragment({
          id: cache.identify({ conversationId, __typename: 'ZRM_Conversation' }),
          fragment: gql`
            fragment TourScheduledUpdate on ZRM_Conversation {
              attributes {
                tourScheduledTimestampMs
                __typename
                applicationMarkedSent
              }
            }
          `,
          data: {
            attributes: {
              tourScheduledTimestampMs,
              __typename: 'ZRM_ConversationAttributes',
              applicationMarkedSent,
            },
          },
        });

        cache.modify({
          id: cache.identify({
            __typename: 'ZRM_Note',
            conversationId,
          }),
          fields: {
            lastUpdatedMs() {
              return new Date().getTime();
            },
          },
        });
      },
      onError() {
        setShowAlertMessage(true);
      },
      onCompleted() {
        setShowAlertMessage(false);
        setInlineFeedbackForTourScheduled(true);
      },
    });
  };

  return (
    <>
      {showAlertMessage && (
        <Alert tone="critical" css={{ marginBottom: 'loose' }}>
          {customizedAlertMessage === '' ? ERROR_MESSAGE : customizedAlertMessage}
        </Alert>
      )}

      {!hasZrmToursEnabled && (
        <>
          <TooltipWrapper wrapInTooltip={isImpersonator} tooltipText={StringConstants.TOOLTIP.IMPERSONATION}>
            <FormField css={{ marginTop: 'tightest', marginBottom: 'tightest' }}>
              <Box
                css={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 'tighter',
                }}
              >
                <Label>Tour details</Label>
                {inlineFeedbackForTourScheduled && (
                  <Box css={{ display: 'flex', alignItems: 'center' }}>
                    <Paragraph textStyle="body-sm" css={{ color: 'green.500' }}>
                      Saved
                    </Paragraph>
                    <IconCheckmarkCircle marginLeft={0.5} fontColor="green500" />
                  </Box>
                )}
              </Box>
              <AdornedInput
                input={
                  <DatePickerInput
                    disabled={isImpersonator}
                    onChange={handleDatePickerInputChange}
                    placeholder="MM/DD/YYYY"
                    value={formattedTourScheduledDate}
                  />
                }
                leftAdornment={
                  <Adornment>
                    <IconCalendarOutline />
                  </Adornment>
                }
              />
            </FormField>
          </TooltipWrapper>

          <FormField css={{ marginTop: 'tightest', marginBottom: 'tightest' }}>
            <TimePicker
              onTimePickerChange={handleTimePickerChange}
              tourScheduledTimestampMs={tourScheduledTimestampMs}
            />
          </FormField>

          {tourScheduledTimestampMs && (
            <TextButton
              tone="neutral"
              disabled={isImpersonator}
              textStyle="body-sm"
              onClick={handleClearTourScheduledTimestampMs}
            >
              Remove tour
            </TextButton>
          )}
        </>
      )}

      <FormField>
        <Box
          css={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: hasZrmToursEnabled ? 0 : 'loose',
          }}
        >
          <Label>Application status</Label>
          {inlineFeedbackForApplicationSent && (
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Paragraph textStyle="body-sm" css={{ color: 'green.500' }}>
                Saved
              </Paragraph>
              <IconCheckmarkCircle marginLeft={0.5} fontColor="green500" />
            </Box>
          )}
        </Box>

        {applicationAlreadySent ? (
          <Box css={{ display: 'flex', alignItems: 'center', marginTop: 'tighter' }}>
            <Checkbox defaultChecked disabled />
            <Label fontType="body" marginLeft={1}>
              Sent to renter using Zillow Applications
            </Label>
          </Box>
        ) : (
          <>
            <TooltipWrapper wrapInTooltip={isImpersonator} tooltipText={StringConstants.TOOLTIP.IMPERSONATION}>
              <StyledLabeledControl
                control={<Checkbox checked={isCheckboxChecked} onChange={handleApplicationSentChange} />}
                controlId="LabeledControl-application-sent"
                disabled={isImpersonator}
                label={<Label>Sent to renter</Label>}
              />
            </TooltipWrapper>
          </>
        )}
      </FormField>

      <FormField>
        <Box
          css={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'tighter',
            marginTop: 'loose',
          }}
        >
          <Label>Notes</Label>
          {inlineFeedbackForNoteUpdated && (
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Paragraph textStyle="body-sm" css={{ color: 'green.500' }}>
                Saved
              </Paragraph>
              <IconCheckmarkCircle marginLeft={0.5} fontColor="green500" />
            </Box>
          )}
        </Box>
        <TooltipWrapper wrapInTooltip={isImpersonator} tooltipText={StringConstants.TOOLTIP.IMPERSONATION}>
          <Textarea
            disabled={isImpersonator}
            onBlur={saveNoteText}
            onChange={handleNoteTextChange}
            placeholder={TEXT_AREA_PLACEHOLDER}
            rows={5}
            value={noteText || ''}
            css={{ resize: 'vertical' }}
          />
        </TooltipWrapper>
      </FormField>
      <Box css={{ display: 'flex', alignItems: 'center', marginTop: 'loose' }}>
        <Paragraph textStyle="body-xs">{PRIVACY_TEXT}</Paragraph>
        <IconLock marginLeft={1} fontColor="coolGray500" size="xs" />
      </Box>
    </>
  );
};

export default Notepad;
